// SPDX-License-Identifier: Apache-2.0

import { jsonArrayMember, jsonMember, jsonObject } from "typedjson";
import { jsonBigIntArrayMember, jsonBigIntMember } from "./bigint";
import Address from "./address";
import EthereumAsset from "./ethereumAsset";
import StellarAsset from "./stellarAsset";
import { NervosAsset, SUDT } from "./nervosAsset";

export type ChannelAsset =
  | ChannelStellarAsset
  | ChannelEthAsset
  | ChannelNervosAsset;
export type Asset = EthereumAsset | StellarAsset | NervosAsset;

@jsonObject()
export class ChannelEthAsset {
  @jsonMember(Address) assetHolder: Address;
  @jsonBigIntMember() chainID: bigint;

  constructor(assetHolder: Address, chainID: bigint) {
    this.assetHolder = assetHolder;
    this.chainID = chainID;
  }
}

@jsonObject()
export class ChannelStellarAsset {
  @jsonMember(String) contractID: string;

  constructor(contractID: string) {
    this.contractID = contractID;
  }
}

@jsonObject()
export class ChannelNervosAsset {
  @jsonBigIntMember() chainID: bigint;
  @jsonMember(SUDT) address: SUDT;

  constructor(address: SUDT, chainID: bigint) {
    this.chainID = chainID;
    this.address = address;
  }
}

@jsonObject
export default class ChannelState {
  @jsonMember({
    deserializer: (json: any) =>
      Array.isArray(json) ? json.map(deserializeChannelAsset) : [],
    serializer: (assets: ChannelAsset[]) => assets.map(serializeChannelAsset),
  })
  assets: ChannelAsset[];
  @jsonArrayMember(Number) backends: number[];
  @jsonBigIntArrayMember() balance: bigint[];
  @jsonBigIntArrayMember() peerBalance: bigint[];
  @jsonMember(Boolean) isFinal: boolean;

  constructor(
    assets: ChannelAsset[],
    backends: number[],
    balance: bigint[],
    peerBalance: bigint[],
    isFinal = false,
  ) {
    this.assets = assets;
    this.backends = backends;
    this.balance = balance;
    this.peerBalance = peerBalance;
    this.isFinal = isFinal;
  }
}

// Helper method to serialize ChannelAsset into JSON
export function serializeChannelAsset(asset: ChannelAsset): any {
  if (asset instanceof ChannelStellarAsset) {
    return {
      asset: {
        assetType: "Stellar",
        contractID: asset.contractID,
      },
    };
  } else if (asset instanceof ChannelEthAsset) {
    return {
      asset: {
        assetType: "Ethereum",
        assetHolder: Address.toJSON(asset.assetHolder),
        chainID: asset.chainID.toString(),
      },
    };
  } else {
    return {
      asset: {
        assetType: "Nervos",
        address: asset.address,
        chainID: asset.chainID.toString(),
      },
    };
  }
}

// Helper method to deserialize JSON into the correct ChannelAsset type
export function deserializeChannelAsset(json: any): ChannelAsset {
  const asset = json.asset; // Unwrap the "asset" key
  if (!asset) {
    console.log(json);
    throw new Error("Malformed asset JSON");
  }

  if (asset.assetType === "Stellar") {
    return new ChannelStellarAsset(asset.contractID);
  } else if (asset.assetType === "Ethereum") {
    return new ChannelEthAsset(
      Address.fromJSON(asset.assetHolder),
      BigInt(asset.chainID),
    );
  } else if (asset.assetType === "Nervos") {
    return new ChannelNervosAsset(asset.address, BigInt(asset.chainID));
  }

  console.log(asset);
  throw new Error("Unknown asset type");
}

// Helper method to serialize ChannelAsset into JSON
export function serializeAsset(asset: Asset): any {
  if (asset instanceof StellarAsset) {
    return {
      asset: {
        assetType: "Stellar",
        code: asset.code,
        name: asset.name,
        type: asset.type,
        contractID: asset.contractID,
        chainID: asset.chainID.toString(),
      },
    };
  } else if (asset instanceof EthereumAsset) {
    return {
      asset: {
        assetType: "Ethereum",
        code: asset.code,
        name: asset.name,
        type: asset.type,
        chainID: asset.chainID.toString(),
        address: Address.toJSON(asset.address),
        assetHolder: Address.toJSON(asset.assetHolder),
      },
    };
  } else {
    return {
      asset: {
        assetType: "Nervos",
        code: asset.code,
        name: asset.name,
        chainID: asset.chainID.toString(),
        address: asset.address,
      },
    };
  }
}

// Helper method to deserialize JSON into the correct ChannelAsset type
export function deserializeAsset(json: any): Asset {
  const asset = json.asset; // Unwrap the "asset" key
  if (!asset) {
    const kind =
      json.assetType ||
      (json.type === "ETH" || json.type === "ERC20"
        ? "Ethereum"
        : json.type === "SUDT" || json.type === "CKB"
          ? "Nervos"
          : "Stellar");

    if (kind === "Ethereum") {
      return new EthereumAsset(
        json.code,
        json.name,
        json.type,
        BigInt(json.chainID),
        json.address ? Address.fromJSON(json.address) : Address.zero(),
        json.assetHolder ? Address.fromJSON(json.assetHolder) : Address.zero(),
      );
    } else if (kind === "Nervos") {
      return new NervosAsset(
        json.code,
        json.name,
        json.decimal,
        BigInt(json.chainID),
        json.address,
        json.type,
      );
    } else {
      return new StellarAsset(
        json.code,
        json.name,
        json.type,
        json.tokenAddress || json.contractID,
        BigInt(json.chainID),
      );
    }
  }

  if (asset.assetType === "Stellar") {
    return new StellarAsset(
      asset.code,
      asset.name,
      asset.type,
      asset.contractID,
      BigInt(asset.chainID),
    );
  } else if (asset.assetType === "Ethereum") {
    return new EthereumAsset(
      asset.code,
      asset.name,
      asset.type,
      BigInt(asset.chainID),
      Address.fromJSON(asset.address),
      Address.fromJSON(asset.assetHolder),
    );
  } else {
    return new NervosAsset(
      asset.code,
      asset.name,
      asset.decimal,
      asset.chainID,
      asset.address,
      asset.type,
    );
  }

  console.log(asset);
  throw new Error("Unknown asset type");
}
